import { retryableLoad } from "@mirage/service-conversation/service/conversationsSync/retryable";
import { Observable, Subject, type Subscription } from "rxjs";

import type {
  ConversationsMetadataSyncInterface,
  ConversationsMetadataSyncInterfaceInternal,
  DataAPI,
} from "@mirage/service-conversation/service/conversationsSync/types";
import type { ConversationMetadata } from "@mirage/service-conversation/types";

// cap for how many recent conversations to load from the API
const MAX_CONVERSATIONS_TO_LOAD = 100;

// sync logic for conversation metadata (e.g. title, last updated, etc.) + listing
export function createConversationsMetadataSync(
  api: DataAPI,
): ConversationsMetadataSyncInterfaceInternal {
  /* cached metadatas, mapped by server conversation ID */
  let cachedMetadatas: Promise<Map<string, ConversationMetadata>> | undefined =
    undefined;
  /* subject that emits latest metadatas */
  const metadatasSubject = new Subject<ConversationMetadata[]>();
  /* set if there is an ongoing refresh that needs to be interrupted if a new refresh is requested */
  let retryOngoingRefresh: (() => void) | undefined = undefined;

  const loadFromAPI = async (): Promise<Map<string, ConversationMetadata>> => {
    // For now, we only load from API. We may choose to first load from storage in the future as an
    // optimization if the network call is slow.
    const apiMetadatas = await api.listMetadata(MAX_CONVERSATIONS_TO_LOAD);
    const metadatas = new Map(
      apiMetadatas.map((metadata) => [metadata.serverConversationId, metadata]),
    );
    metadatasSubject.next(
      sortedConversationMetadatas(Array.from(metadatas.values())),
    );
    return metadatas;
  };

  // get cached metadatas, may be modified in place
  const getMetadatas = async (): Promise<Map<string, ConversationMetadata>> => {
    if (!cachedMetadatas) {
      cachedMetadatas = loadFromAPI();
    }
    return cachedMetadatas;
  };

  const getConversationsList: ConversationsMetadataSyncInterface["getConversationsList"] =
    async () => {
      const metadatas = await getMetadatas();
      return sortedConversationMetadatas(Array.from(metadatas.values()));
    };

  const refreshConversationsList: ConversationsMetadataSyncInterface["refreshConversationsList"] =
    async () => {
      // using retryableLoad here so an update in the middle of a refresh will discard the ongoing
      // refresh to avoid races where newly created conversations will be deleted by an ongoing
      // refresh.
      const { promise, retry } = retryableLoad(loadFromAPI);
      retryOngoingRefresh = retry;

      // wait for the promise to resolve
      const metadatas = await promise;
      retryOngoingRefresh = undefined;

      // update cached metadatas + return
      cachedMetadatas = promise;
      return sortedConversationMetadatas(Array.from(metadatas.values()));
    };

  const getConversationMetadata: ConversationsMetadataSyncInterface["getConversationMetadata"] =
    async (serverConversationId) => {
      const existingMetadatas = await getMetadatas();
      const existingMetadata = existingMetadatas.get(serverConversationId);
      if (existingMetadata) {
        return existingMetadata;
      }
      // not currently cached, load from API directly
      return await api.getMetadata(serverConversationId);
    };

  const updateConversationMetadata: ConversationsMetadataSyncInterface["updateConversationMetadata"] =
    async (serverConversationID, metadata) => {
      const metadatas = await getMetadatas();
      const existingMetadata = metadatas.get(serverConversationID);
      if (!existingMetadata) {
        throw new Error(`Conversation not found: ${serverConversationID}`);
      }
      const updatedMetadata = { ...existingMetadata, ...metadata };
      updateCachedConversationMetadata(serverConversationID, updatedMetadata);

      // queue an API update but don't wait for it to complete
      api.updateMetadata(serverConversationID, metadata);

      return updatedMetadata;
    };

  const updateCachedConversationMetadata: ConversationsMetadataSyncInterfaceInternal["updateCachedConversationMetadata"] =
    async (serverConversationID, metadata) => {
      const metadatas = await getMetadatas();
      metadatas.set(serverConversationID, metadata);

      // update cached metadatas + interrupt any ongoing refresh to avoid races where refresh
      // wipes out an update
      retryOngoingRefresh?.();
      metadatasSubject.next(
        sortedConversationMetadatas(Array.from(metadatas.values())),
      );
    };

  const deleteCachedConversationMetadatas: ConversationsMetadataSyncInterfaceInternal["deleteCachedConversationMetadatas"] =
    async (serverConversationIDs) => {
      const metadatas = await getMetadatas();
      for (const serverConversationID of serverConversationIDs) {
        metadatas.delete(serverConversationID);
      }
      retryOngoingRefresh?.();
      metadatasSubject.next(
        sortedConversationMetadatas(Array.from(metadatas.values())),
      );
    };

  const metadatasObservable = (): Observable<ConversationMetadata[]> => {
    return new Observable((subscriber) => {
      let subscription: Subscription | undefined;

      // when subscribing, start by sending them the initial state
      getConversationsList()
        .then((metadatas) => {
          subscriber.next(metadatas);
          subscription = metadatasSubject.subscribe(subscriber);
          return subscription;
        })
        .catch((error) => {
          return subscriber.error(error);
        });

      return () => {
        subscription?.unsubscribe();
      };
    });
  };

  return {
    getConversationsList,
    refreshConversationsList,
    getConversationMetadata,
    updateConversationMetadata,
    updateCachedConversationMetadata,
    deleteCachedConversationMetadatas,
    metadatasObservable,
  };
}

/**
 * Sort conversation metadatas by most recently updated first.
 */
function sortedConversationMetadatas(
  metadatas: ConversationMetadata[],
): ConversationMetadata[] {
  return metadatas.sort((a, b) => b.updated.getTime() - a.updated.getTime());
}
